/** WebSocket payload parsers: shared coercers, bid entities, transactions / images / offers, auction fields, locationData guards and the
 * top-level property projection. `data.<Field>` reads against the `coerceRecord` locals are always wrapped in `optStr` / `optBool` -- both
 * accept `unknown` and return `T | undefined`, so the access cannot raise a TypeError. `QUAL-RECORD-UNKNOWN-ACCESS-001` recognises both by
 * CALL-SITE IDENTIFIER, never by module specifier -- but that rule NO LONGER REACHES THIS FILE. It relocated here from
 * `tools/websocket/` on 2026-08-28 (QUAL-NO-NONFLOW-TOOL-IMPORTS-001 Cure A), and that rule's fileGlob is a layer brace
 * list omitting `components`, so the wrapping discipline above is now convention held by this docstring rather than by a
 * matcher. Filed as its own countermeasure (issue d4b500fe) against widening the rule; do not read the citation as live
 * enforcement until it lands. */
import { parseUserId, parseUserIdOrUndefined, parsePropertyId } from '@federa/utils/strings/parse';
import { coerceFormattedNumber as toNumber } from '@federa/const/coerce-number';
import { coerceRecord, narrowObjectArray } from '@federa/utils/data/coerce-record';
import { coerceStringOrNumber } from '@federa/utils/data/coerce-string-or-number';
import { optBool } from '@federa/utils/data/opt-bool';
import { guardedArrayTransform } from '@federa/utils/data/array-utils';
import { resolveRecordId } from '@federa/utils/data/resolve-record-id';
import { parseDate } from '@federa/const/date-parse-format';
import { LATITUDE_ABS_MAX, LONGITUDE_ABS_MAX } from '@federa/const/geo-coordinate-bounds';
import { sortByTimestampDesc, extractTimestamp } from '@federa/utils/dates/timestamp';
import { isPlainObject, isRecordValue } from '@federa/const/record-guards';
import { firstPresent } from '@federa/const/dual-spelling';
import { FORM_FIELDS } from '@federa/const/reducer-actions';
import {
	DAYS_TO_CANCEL_FIELD_KEYS,
	DAYS_TO_CLOSE_FIELD_KEYS,
	OFFER_AMOUNT_FIELD_KEYS,
	OFFER_STATUS_FIELD_KEYS,
} from '@federa/const/offer-legacy-field-keys';
import { AUCTION_STATUS_FIELD_KEYS, DEAL_STATUS_FIELD, DEAL_STATUS_FIELD_KEYS } from '@federa/const/transaction-status-field-keys';
import { DB_COLUMN_NAMES } from '@federa/const/column-names';
import { DB_COLUMNS } from '@federa/const/db-columns';
import { TRAVEL_MODES, WALKABILITY_GRADES } from '@federa/const/property-neighborhood-vocabulary';
import { asStringOrUndefined, coerceString, isValidString, nonEmptyStringOrUndefined, toSafeString } from '@federa/const/string-guards';
import { asNumberOrUndefined } from '@federa/const/number-guards';
import { hasLength } from '@federa/const/array-guards';
import type { Bid } from '@federa/types/properties-auction';
import type { CommuteDestination, NearbyAmenitiesData, WalkabilityMetrics } from '@federa/types/properties-neighborhood';
import type { Offer, OfferStatus } from '@federa/types/properties-offers';
import type { Property, PropertyDetails } from '@federa/types/properties';
import type { PropertyImage } from '@federa/types/properties-image';
import type { Transaction } from '@federa/types/properties-transaction';

const PICK_AUCTION1 = 'Auction1';

/** An untrusted payload read as a record: its own members when it IS one, an EMPTY record otherwise. Every reader below
 *  takes the empty record rather than a null, because the field reads that follow are all `opt*`-wrapped. */
function recordOrEmpty(raw: unknown): Record<string, unknown> {
	return coerceRecord(raw) ?? {};
}

/**
 * TRANSFORM-AND-DEFAULT wrapper over `firstPresent`, NOT a second implementation of it. The multi-key wire-alias scan
 * -- first key whose value is neither `null` nor `undefined` -- is the SSOT's, spelled once; only the coercion and the
 * miss value are this file's. A sibling variadic respelling of the same scan (`pick`) stood beside it in this module,
 * under its own name, while `firstPresent` was already imported here and used a dozen lines away: two spellings of one
 * decision, one of which the paired `QUAL-DUAL-SPELLING-COALESCE-SSOT-001` rule cannot see because it hunts
 * hand-rolled `??` EXPRESSIONS rather than a hand-rolled loop.
 */
function findFirstValue<T>(
	data: Record<string, unknown>,
	keys: string[],
	options: { transform: (value: unknown) => T; defaultValue: T },
): T {
	const { transform, defaultValue } = options;
	const value = firstPresent(data, ...keys);
	return value === undefined ? defaultValue : transform(value);
}

function tryParseDate(value: unknown): string | null {
	const date = parseDate(
		typeof value === 'string' || typeof value === 'number' || value instanceof Date || value == null ? value : undefined,
	);
	return date?.toISOString() ?? null;
}

function createArrayParser<T>(itemParser: (raw: unknown) => T | null): (raw: unknown) => T[] {
	return (raw: unknown): T[] => guardedArrayTransform<unknown, T>(raw, itemParser);
}

const getStr = (data: Record<string, unknown>, ...keys: string[]): string =>
	findFirstValue(data, keys, { transform: String, defaultValue: '' });

const getBool = (data: Record<string, unknown>, ...keys: string[]): boolean =>
	findFirstValue(data, keys, { transform: Boolean, defaultValue: false });

const optStrFrom = (data: Record<string, unknown>, ...keys: string[]): string | undefined =>
	findFirstValue(data, keys, { transform: String, defaultValue: undefined });

const posNum = (v: unknown): number | undefined => {
	const n = toNumber(v);
	return Number.isFinite(n) && n > 0 ? n : undefined;
};

/** Deliberately DROPS falsy values (0, false, '', null) -- distinct from a typeof-narrowing optional-string helper, which preserves only real strings. */
const optStr = (v: unknown): string | undefined => (v ? String(v) : undefined);

/**
 * DEGRADES on a drifted row -- it never throws. Every other member of a bid already degraded (`getStr` -> `''`,
 * `toNumber` -> `NaN`, `getBool` -> `false`, `tryParseDate` -> `null`); the two BRANDED members did not, because
 * `parseUserId` / `parsePropertyId` are `brandedIdParser` over `parseBrandedId` (`utils/strings/parse.ts`), which
 * THROWS on the empty string `getStr` produces for an absent key. Nothing on the inbound path caught it --
 * `handleBids` / `handleNewBid` / `handleHighestBid` / `handlePropertyDetailDataResponse` are bare, and
 * `parseAndDispatchMessage` (`tools/websocket/ws-message-parse.ts`) invokes the handler bare -- so ONE subject-less
 * row escaped `onmessage` and silently dropped the whole auction frame, on the property page and on the explore feed
 * alike (`parseProperty` -> `parseRelatedData` -> `parseBids`).
 *
 * The gate is read-then-check rather than a try/catch: a catch would also swallow a genuine defect raised deeper in
 * the row, and the condition the branded parsers reject is exactly "absent", which this file can answer for itself.
 * `Bid.userId` and `Bid.propertyId` stay REQUIRED and branded -- widening them would push this hole into every
 * consumer instead of closing it.
 */
export function parseBid(raw: unknown): Bid | null {
	const data = coerceRecord(raw);
	if (!data) return null;
	const rawUserId = getStr(data, 'userId', 'UserId', 'UserID');
	const rawPropertyId = getStr(data, 'propertyId', 'PropertyId', 'PropertyID');
	if (!rawUserId || !rawPropertyId) return null;
	return {
		id: getStr(data, 'id', 'ID'),
		amount: toNumber(firstPresent(data, 'amount', 'Amount')),
		userId: parseUserId(rawUserId),
		timestamp: tryParseDate(firstPresent(data, 'timestamp', 'Timestamp')),
		originalCompetingBid: toNumber(firstPresent(data, 'originalCompetingBid', 'OriginalCompetingBid')),
		accepted: getBool(data, 'accepted', 'Accepted'),
		winner: getBool(data, 'winner', 'Winner'),
		winning: getBool(data, 'winning', 'Winning'),
		competingBid: getBool(data, 'competingBid', 'CompetingBid'),
		transactionId: getStr(data, 'transactionId', 'TransactionId', 'TransactionID'),
		propertyId: parsePropertyId(rawPropertyId),
	} satisfies Bid;
}

export function parseBids(raw: unknown): Bid[] {
	if (!Array.isArray(raw)) return [];
	const bids = raw.map((item) => parseBid(item)).filter((item): item is Bid => Boolean(item && item.accepted));
	return sortByTimestampDesc(bids, (bid) => extractTimestamp(null, bid.timestamp));
}

function extractCoreTransactionFields(
	data: Record<string, unknown>,
): Pick<Transaction, 'id' | 'listPrice' | 'auction1' | 'auctionStartDateTime' | 'status' | 'addedTime'> {
	const auctionFlag = firstPresent(data, ...AUCTION_STATUS_FIELD_KEYS, PICK_AUCTION1, 'auction1');
	return {
		id: resolveRecordId(data) ?? '',
		listPrice: toNumber(firstPresent(data, 'List_Price', 'listPrice', 'list_price')),
		auction1: Boolean(auctionFlag),
		auctionStartDateTime: tryParseDate(firstPresent(data, 'auction_start_date_time', 'auctionStartDateTime')),
		status: String(firstPresent(data, ...AUCTION_STATUS_FIELD_KEYS, DEAL_STATUS_FIELD, 'status') ?? ''),
		addedTime: tryParseDate(firstPresent(data, 'Added_Time', 'addedTime', DB_COLUMNS.CREATED_AT)),
	};
}

function extractOptionalTransactionFields(raw: unknown): Partial<Transaction> {
	const data = recordOrEmpty(raw);
	return {
		tags: optStr(firstPresent(data, 'Tags', 'tags')),
		dealStatus: optStr(firstPresent(data, ...DEAL_STATUS_FIELD_KEYS)),
		dealType: optStr(firstPresent(data, 'Deal_Type', 'deal_type')),
		listingClearTitle: optBool(data.listing_clear_title),
		propertyRemarks1: optStr(data.Property_Remarks1 || data.property_remarks),
		propertyType: optStr(data.Property_Type ?? data.what_type_of_property),
		securityDepositAsPercentage: optStr(data.security_deposit_as_percentage),
		securityDepositPreference: optStr(data.security_deposit_preference),
		maximumSecurityDeposit: optStr(data.maximum_security_deposit),
		mustCloseByDateTime: optStr(firstPresent(data, 'Must_Close_By_Date_Time', 'must_close_by_date_time')),
		showingSchedule: optStr(firstPresent(data, 'Showing_Schedule', 'showing_schedule')),
		/* Carried so handlers can authorize off the active transaction without an extra D1 round-trip. */
		addedUserId: optStr(firstPresent(data, 'added_user_id', 'Added_User_ID')),
	};
}

function parseTransaction(raw: unknown): Transaction | null {
	const data = coerceRecord(raw);
	if (!data) return null;
	return {
		...extractCoreTransactionFields(data),
		...extractOptionalTransactionFields(data),
	} satisfies Transaction;
}

const parseTransactionArray = createArrayParser(parseTransaction);

function parseImages(raw: unknown): PropertyImage[] {
	return narrowObjectArray<PropertyImage>(raw, (data) => {
		const urlValue = firstPresent(data, 'url', 'Url', 'URL');
		if (!urlValue) return null;
		const url = toSafeString(urlValue);
		if (!url) return null;
		return {
			id: resolveRecordId(data) ?? '',
			url,
		};
	});
}

function parseOfferDates(raw: unknown): Pick<Offer, 'createdAt' | 'updatedAt'> {
	const data = recordOrEmpty(raw);
	return {
		createdAt: tryParseDate(firstPresent(data, 'createdAt', DB_COLUMNS.CREATED_AT, 'CreatedAt')) ?? undefined,
		updatedAt: tryParseDate(firstPresent(data, 'updatedAt', DB_COLUMN_NAMES.MODIFIED_AT, 'UpdatedAt')) ?? undefined,
	};
}

function parseOfferNumericFields(
	raw: unknown,
): Pick<Offer, 'daysToClose' | 'daysToCancel' | 'daysToDeposit' | 'securityDeposit' | 'deposit'> {
	const d = recordOrEmpty(raw);
	return {
		// PRECEDENCE CORRECTED 2026-09-04, the same BEHAVIOUR change `parseOffer` below took for the amount pair on
		// 2026-08-24 and for the same reason: both rows read the legacy Pascal half FIRST, and that half is a DERIVED
		// MIRROR `normalizeOfferToLegacy` writes from the canonical column and then never refreshes, so legacy-first
		// served the stale value once an offer's terms had moved. The camelCase alias stays LAST -- it is a site-specific
		// third spelling, not a half of the governed pair.
		daysToClose: coerceStringOrNumber(firstPresent(d, ...DAYS_TO_CLOSE_FIELD_KEYS, 'daysToClose')),
		daysToCancel: coerceStringOrNumber(firstPresent(d, ...DAYS_TO_CANCEL_FIELD_KEYS, 'daysToCancel')),
		daysToDeposit: posNum(d.days_to_deposit),
		securityDeposit: posNum(firstPresent(d, FORM_FIELDS.SECURITY_DEPOSIT, 'Security_Deposit')),
		deposit: posNum(firstPresent(d, 'Deposit', 'deposit')),
	};
}

function parseOffer(raw: unknown): Offer | null {
	const data = coerceRecord(raw);
	if (!data) return null;
	// PRECEDENCE CORRECTED 2026-08-24 and it is a BEHAVIOUR change, not a spelling one: this read was legacy-FIRST while
	// its `status` sibling four lines down was canonical-first -- two readers of ONE row shape resolving one pair by
	// opposite orders. `const/offer-legacy-field-keys.ts` records the writer-side evidence that settles it; the short
	// form is that `Offer_Amount` is a DERIVED MIRROR `normalizeOfferToLegacy` writes from `offer_amount` and then never
	// refreshes, so legacy-first served the stale amount whenever an offer's amount had moved.
	const amount = toNumber(firstPresent(data, ...OFFER_AMOUNT_FIELD_KEYS, 'amount', 'Amount'));
	if (!Number.isFinite(amount)) return null;
	const rawUserId = optStrFrom(data, 'userId', 'buyer_id', 'added_user_id');
	return {
		id: getStr(data, 'ID', 'id'),
		amount,
		status: optStrFrom(data, ...OFFER_STATUS_FIELD_KEYS, 'status') as OfferStatus | undefined,
		userId: parseUserIdOrUndefined(rawUserId),
		...parseOfferDates(data),
		...parseOfferNumericFields(data),
		fundingSource: optStrFrom(data, FORM_FIELDS.FUNDING_SOURCE, 'Funding_Source'),
	} satisfies Offer;
}

const parseOffers = createArrayParser(parseOffer);

function parseRelatedData(raw: unknown): {
	matchingActiveTransaction: Transaction | null;
	transactionsListActive: Transaction[];
	transactionsList: Transaction[];
	highestBid: Bid | undefined;
	bids: Bid[];
	offersList: Offer[];
	bestAQO: Offer | null;
	imagesList: PropertyImage[];
} {
	const data = recordOrEmpty(raw);
	return {
		matchingActiveTransaction: parseTransaction(data.matchingActiveTransaction),
		transactionsListActive: parseTransactionArray(data.transactions_list_active),
		transactionsList: parseTransactionArray(data.transactions_list),
		highestBid: parseBid(data.highestBid) ?? undefined,
		bids: parseBids(data.bids),
		offersList: parseOffers(data.offers_list),
		bestAQO: parseOffer(data.bestAQO),
		imagesList: parseImages(data.images_list),
	};
}

/** Members are `?: unknown` because the payload arrives off the wire and each read owns its gate; the two `auction1` spellings and the
 * `auctionExteded` typo are real upstream vocabulary, not a defensive superset. TYPE ALIAS so the implicit index signature keeps the
 * whole-payload pass assignable. */
type WsAuctionSourceFields = {
	auction_status?: unknown;
	Auction1?: unknown;
	auction1?: unknown;
	auctionStateOnFinished?: unknown;
	auctionExteded?: unknown;
	auctionExtended?: unknown;
};

/** Parse auction extended field, handling typo variant 'auctionExteded'. */
function parseAuctionExtended(data: Pick<WsAuctionSourceFields, 'auctionExteded' | 'auctionExtended'>): boolean | undefined {
	if (typeof data.auctionExteded === 'boolean') return data.auctionExteded;
	if (typeof data.auctionExtended === 'boolean') return data.auctionExtended;
	return undefined;
}

function buildAuctionFields(
	data: WsAuctionSourceFields,
	matchingActiveTransaction: Transaction | null,
): Pick<Property, 'auction1' | 'auctionStateOnFinished' | 'auctionExtended' | 'auctionStartDateTime'> {
	return {
		auction1: Boolean(data.auction_status ?? firstPresent(data, PICK_AUCTION1, 'auction1') ?? matchingActiveTransaction?.auction1),
		auctionStateOnFinished: asStringOrUndefined(data.auctionStateOnFinished),
		auctionExtended: parseAuctionExtended(data),
		auctionStartDateTime: matchingActiveTransaction?.auctionStartDateTime ?? undefined,
	};
}

// The `locationData` predicates below replace unsafe casts at the WS gateway boundary with runtime structural checks, so a malformed upstream payload fails CLOSED instead of crossing mistyped.

/** Membership views over the value SSOT (`const/property-neighborhood-vocabulary.ts`) the `TravelMode` and
 *  `WalkabilityMetrics['grade']` unions derive from -- widening the vocabulary moves the type and this guard together. */
const TRAVEL_MODE_SET: ReadonlySet<string> = new Set(TRAVEL_MODES);
const WALKABILITY_GRADE_SET: ReadonlySet<string> = new Set(WALKABILITY_GRADES);

/** Local alias for the shared array-excluding record predicate; the many `isRecord(...)` call sites below read better unqualified. */
function isRecord(value: unknown): value is Record<string, unknown> {
	return isPlainObject(value);
}

/** `satisfies` ties every member to a REAL `WalkabilityMetrics` key: this list restates the interface's numeric field
 *  names, and without the constraint a field rename in `types/properties-neighborhood.ts` would leave the guard
 *  silently checking a key the payload no longer carries -- rejecting every well-formed scorecard. */
const WALKABILITY_NUMBER_FIELDS = [
	'score',
	'amenitiesWithin5Min',
	'amenitiesWithin10Min',
	'amenitiesWithin15Min',
	'transitStops',
	'parks',
	'restaurants',
	'grocery',
] as const satisfies readonly (keyof WalkabilityMetrics)[];

function hasAllNumberFields(record: Record<string, unknown>, fields: readonly string[]): boolean {
	for (const f of fields) {
		if (typeof record[f] !== 'number') return false;
	}
	return true;
}

function isWalkabilityMetrics(value: unknown): value is WalkabilityMetrics {
	if (!isRecord(value)) return false;
	if (typeof value.grade !== 'string' || !WALKABILITY_GRADE_SET.has(value.grade)) return false;
	if (typeof value.description !== 'string') return false;
	return hasAllNumberFields(value, WALKABILITY_NUMBER_FIELDS);
}

function isCommuteDestination(value: unknown): value is CommuteDestination {
	if (!isRecord(value)) return false;
	if (typeof value.id !== 'string') return false;
	if (typeof value.label !== 'string') return false;
	if (typeof value.address !== 'string') return false;
	if (typeof value.travelMode !== 'string' || !TRAVEL_MODE_SET.has(value.travelMode)) return false;
	if (typeof value.durationMinutes !== 'number') return false;
	if (typeof value.distanceMiles !== 'number') return false;
	return true;
}

function isCommuteDestinationArray(value: unknown): value is CommuteDestination[] {
	return Array.isArray(value) && value.every(isCommuteDestination);
}

function isNearbyAmenitiesData(value: unknown): value is NearbyAmenitiesData {
	if (!isRecord(value)) return false;
	if (!Array.isArray(value.places)) return false;
	if (!isWalkabilityMetrics(value.walkability)) return false;
	if (!isRecord(value.propertyLocation)) return false;
	if (typeof value.propertyLocation.lat !== 'number') return false;
	if (typeof value.propertyLocation.lng !== 'number') return false;
	if (typeof value.lastUpdated !== 'string') return false;
	return true;
}

function parseLocationData(data: Record<string, unknown>): Property['locationData'] {
	if (!('locationData' in data)) return undefined;
	const raw = data.locationData;
	if (!isPlainObject(raw)) return undefined;
	const loc = raw;
	return {
		walkability: isWalkabilityMetrics(loc.walkability) ? loc.walkability : undefined,
		commutes: isCommuteDestinationArray(loc.commutes) ? loc.commutes : undefined,
		nearbyAmenities: isNearbyAmenitiesData(loc.nearbyAmenities) ? loc.nearbyAmenities : undefined,
	};
}

const orUndef = <T>(arr: T[]): T[] | undefined => (hasLength(arr) ? arr : undefined);

/** WGS84 range check over an untrusted socket payload -- hence the `Number.isNaN` arms the places-side validator does
 *  not need, its input having already passed `parseNumberLike`. The bounds come from the const SSOT rather than the
 *  four literals they were spelled as. */
function isValidCoordinate(lat: number, lng: number): boolean {
	if (Number.isNaN(lat) || Number.isNaN(lng)) return false;
	return lat >= -LATITUDE_ABS_MAX && lat <= LATITUDE_ABS_MAX && lng >= -LONGITUDE_ABS_MAX && lng <= LONGITUDE_ABS_MAX;
}

export function parseRemainingTime(raw: unknown): {
	remainingSeconds?: number;
	auctionEndUtc: string | null;
} {
	const data = recordOrEmpty(raw);
	const remaining = data.remainingTime;
	if (remaining && typeof remaining === 'object') {
		const r = remaining as Record<string, unknown>;
		const rawSec = firstPresent(r, 'totalSeconds', 'total_seconds');
		const secs = typeof rawSec === 'number' ? rawSec : typeof rawSec === 'string' ? Number.parseInt(rawSec, 10) : Number.NaN;
		return {
			remainingSeconds: Number.isNaN(secs) ? undefined : secs,
			auctionEndUtc: tryParseDate(firstPresent(r, 'UTCDateTime', 'utcDateTime', 'utc_date_time')),
		};
	}
	return {
		remainingSeconds: asNumberOrUndefined(data.remainingSeconds),
		auctionEndUtc: null,
	};
}

/** A coordinate pair a reader may trust, or `null` when the payload carried none that passes the range check. */
type ValidatedCoordinates = { latitude: number; longitude: number } | null;

/** The pair, but ONLY when both halves pass the WGS84 check -- the one place an untrusted pair becomes a trusted one. */
function validatedCoordinates(lat: number, lng: number): ValidatedCoordinates {
	return isValidCoordinate(lat, lng) ? { latitude: lat, longitude: lng } : null;
}

function tryParseNumericCoords(latField: unknown, lngField: unknown): ValidatedCoordinates {
	// Accepts a NUMERIC STRING as well as a number: a number-only guard rejected PropertyDO's preferred field and zeroed coordinates.
	if (latField === null || latField === undefined || (typeof latField !== 'number' && typeof latField !== 'string')) return null;
	const lat = toNumber(latField);
	const lng = toNumber(lngField);
	return validatedCoordinates(lat, lng);
}

function tryParseTupleCoords(lngField: unknown): ValidatedCoordinates {
	if (typeof lngField !== 'string') return null;
	const match = lngField.match(/\(?\s*([-+]?[0-9]*\.?[0-9]+)\s*,\s*([-+]?[0-9]*\.?[0-9]+)\s*\)?/);
	if (!match) return null;
	const lat = Number.parseFloat(match[1] as string);
	const lng = Number.parseFloat(match[2] as string);
	return validatedCoordinates(lat, lng);
}

function parseCoordinates(raw: unknown): {
	latitude?: number;
	longitude?: number;
} {
	const detailsRaw = recordOrEmpty(raw);
	const latField = detailsRaw.property_latitude ?? detailsRaw.latitude;
	const lngField = detailsRaw.property_longitude ?? detailsRaw.longitude;
	return tryParseNumericCoords(latField, lngField) ?? tryParseTupleCoords(lngField) ?? {};
}

function calculateAskPrice(rawDetails: unknown, rawData: unknown, matchingTransaction: Transaction | null): number {
	const detailsRaw = recordOrEmpty(rawDetails);
	const data = recordOrEmpty(rawData);
	return toNumber(detailsRaw.AskPrice ?? data.askPrice ?? matchingTransaction?.listPrice ?? 0);
}

/** Tolerates the numeric-string form. */
function optNumber(value: unknown): number | undefined {
	if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;
	if (isValidString(value)) {
		const parsed = Number(value);
		return Number.isFinite(parsed) ? parsed : undefined;
	}
	return undefined;
}

/** Half baths count as 0.5 -- what schema.org `numberOfBathroomsTotal` means. Kept module-scope so the details builder stays within the complexity cap. */
function combinedBathrooms(structure: Record<string, unknown>): number | undefined {
	const fullBaths = optNumber(structure.total_full_baths);
	const halfBaths = optNumber(structure.total_half_baths);
	return fullBaths === undefined && halfBaths === undefined ? undefined : (fullBaths ?? 0) + (halfBaths ?? 0) * 0.5;
}

/** Address/identity facts off the raw envelope (casing is deliberately dual). Kept module-scope so the details builder stays within the complexity cap. */
function detailsIdentityFacts(
	detailsRaw: Record<string, unknown>,
): Pick<PropertyDetails, 'address' | 'last' | 'fullAddress' | 'state' | 'coverPhoto' | 'city' | 'zipCode'> {
	return {
		// TWO SSOTs on one line, and the raw chain bypassed both: `firstPresent` is the dual-key `??` read this file
		// already spends on the very next line, and `coerceString` is the always-coerce-with-empty-default flattening.
		address: coerceString(firstPresent(detailsRaw, 'Address', 'Full_Address')),
		last: toNumber(firstPresent(detailsRaw, 'Last', 'last')),
		fullAddress: detailsRaw.Full_Address ? String(detailsRaw.Full_Address) : undefined,
		state: nonEmptyStringOrUndefined(detailsRaw.State) ?? nonEmptyStringOrUndefined(detailsRaw.state_province),
		coverPhoto: detailsRaw.Cover_Photo ? String(detailsRaw.Cover_Photo) : undefined,
		city: nonEmptyStringOrUndefined(detailsRaw.district_city),
		zipCode: nonEmptyStringOrUndefined(detailsRaw.postal_code),
	};
}

/** Maps the PropertyDO `property_details` envelope onto `PropertyDetails`. The nulls are REAL for some properties -- a record with no
 * `structure` row genuinely has none -- so this fills what exists and leaves the rest undefined; do not read one property's nulls as proof
 * the field is absent from the contract, a mistake that once hid a whole class of dropped structure facts. Casing is deliberately dual. */
function buildPropertyDetails(raw: unknown, id: string): PropertyDetails {
	const detailsRaw = recordOrEmpty(raw);
	const coordinates = parseCoordinates(detailsRaw);
	const structure = recordOrEmpty(detailsRaw.structure);
	const land = recordOrEmpty(detailsRaw.land);
	return {
		id,
		...detailsIdentityFacts(detailsRaw),
		latitude: coordinates.latitude,
		longitude: coordinates.longitude,
		propertyType: nonEmptyStringOrUndefined(detailsRaw.property_type) ?? nonEmptyStringOrUndefined(structure.structure_type),
		bedrooms: optNumber(structure.total_bedrooms),
		bathrooms: combinedBathrooms(structure),
		squareFootage: optNumber(structure.total_sqft) ?? optNumber(structure.above_grade_finished_sqft),
		yearBuilt: optNumber(structure.year_built),
		lotSize: optNumber(land.lot_size_sqft),
	};
}

/** Fails CLOSED on an absent / non-object envelope. */
function extractPropertyData(raw: unknown): {
	detailsRaw: Record<string, unknown>;
	id: string;
} | null {
	const data = coerceRecord(raw);
	if (!data) return null;
	const details = data.property_details;
	if (!isRecordValue(details)) return null;
	const detailsRaw = details;
	const id = resolveRecordId(detailsRaw);
	return id ? { detailsRaw, id } : null;
}

/** Defaults to the payload position on missing/non-numeric input. */
function parseBestMatchOrder(data: Record<string, unknown>, defaultIndex: number): number {
	if (typeof data.bestMatchOrder === 'number') return data.bestMatchOrder;
	if (typeof data.bestMatchOrder === 'string') {
		const parsed = Number.parseInt(data.bestMatchOrder, 10);
		if (!Number.isNaN(parsed)) return parsed;
	}
	return defaultIndex;
}

function parseRemarks(data: Record<string, unknown>, detailsRaw: Record<string, unknown>): string | undefined {
	if (data.remarks) return String(data.remarks);
	if (detailsRaw.property_remarks || detailsRaw.Property_Remarks1)
		return String(detailsRaw.property_remarks ?? detailsRaw.Property_Remarks1);
	return undefined;
}

function parseProperty(raw: unknown, index = 0): Property | null {
	const data = coerceRecord(raw);
	if (!data) return null;

	const extracted = extractPropertyData(data);
	if (!extracted) return null;
	const { detailsRaw, id } = extracted;

	const propertyDetails = buildPropertyDetails(detailsRaw, id);
	const related = parseRelatedData(data);
	const { remainingSeconds, auctionEndUtc } = parseRemainingTime(data);
	const auctionFields = buildAuctionFields(data, related.matchingActiveTransaction);

	return {
		id,
		bestMatchOrder: parseBestMatchOrder(data, index),
		propertyDetails,
		imagesList: related.imagesList,
		matchingActiveTransaction: related.matchingActiveTransaction,
		transactionsListActive: orUndef(related.transactionsListActive),
		transactionsList: orUndef(related.transactionsList),
		highestBid: related.highestBid,
		bids: orUndef(related.bids),
		offersList: orUndef(related.offersList),
		bestAQO: related.bestAQO ?? undefined,
		auctionState: optStr(data.auctionState),
		auctionManagerId: data.auctionManagerId ? parseUserId(String(data.auctionManagerId)) : undefined,
		...auctionFields,
		askPrice: calculateAskPrice(detailsRaw, data, related.matchingActiveTransaction),
		last: propertyDetails.last,
		address: optStr(detailsRaw.Address),
		remarks: parseRemarks(data, detailsRaw),
		remainingSeconds,
		auctionEndUtc,
		locationData: parseLocationData(data),
		interestedUsersCount: asNumberOrUndefined(data.interestedUsersCount),
	};
}

export { parseProperty };
